import { type CreditV1GetMySubscriptionReply, getMySubscription } from '@/app/apis';
import { useStore } from '@/app/providers/store';
import type { SubscriptionInfo } from '@/app/stores/slices/user-info';
import type { SubsPlanType } from '@/app/types';
import { useFetch } from './use-fetch';

/**
 * 检查新获取的订阅信息是否与当前存储的订阅信息相同
 * @param newData - 从API获取的新订阅数据
 * @param currentSubscription - 当前存储在状态中的订阅信息
 * @returns {boolean} true表示订阅信息未变化，false表示有变化需要更新
 */
const isSubscriptionUnchanged = (
  newData: CreditV1GetMySubscriptionReply | undefined,
  currentSubscription: SubscriptionInfo | null,
): boolean => {
  // 如果没有新订阅信息，认为未变化
  if (!newData?.subscription) {
    return true;
  }
  // 如果没有当前订阅信息，认为有变化
  if (!currentSubscription) {
    return false;
  }
  const newSubscription = newData.subscription;
  // 比较关键字段是否相同，只有当所有关键字段都相同时才认为订阅信息未变化
  return (
    newSubscription.id === currentSubscription.id &&
    newSubscription.status === currentSubscription.status &&
    newSubscription.period_start === currentSubscription.period_start &&
    newSubscription.period_end === currentSubscription.period_end &&
    newSubscription.billing_period === currentSubscription.billing_period &&
    newSubscription.tier === currentSubscription.tier &&
    newSubscription.user_last_period_end === currentSubscription.user_last_period_end
  );
};

export const useSubscription = () => {
  const {
    subscription,
    isUserInfoInitialized,
    updateSubscriptionInfo,
    updateActiveTeamBundleProduct,
    updateNextPlan,
    updateIsSubscriptionInitialized,
    updateIsGiftPlan,
  } = useStore((state) => ({
    subscription: state.subscription,
    isUserInfoInitialized: state.isUserInfoInitialized,
    updateSubscriptionInfo: state.updateSubscriptionInfo,
    updateActiveTeamBundleProduct: state.updateActiveTeamBundleProduct,
    updateNextPlan: state.updateNextPlan,
    updateIsSubscriptionInitialized: state.updateIsSubscriptionInitialized,
    updateIsGiftPlan: state.updateIsGiftPlan,
  }));

  const { run: updateSubscription, runAsync: updateSubscriptionAsync } = useFetch(getMySubscription, {
    manual: true,
    onSuccess: ({ data }) => {
      /**
       * 订阅信息更新逻辑：
       * 1. 接口未返回 subscription（如切换到无订阅的空间）：清空 store，避免残留上一个空间的旧值
       * 2. 有 subscription 且与当前不同：更新 store；同时同步 nextPlan
       * 3. 有 subscription 但未变化：保持原引用，避免无意义 set
       */
      if (!data?.subscription) {
        if (subscription) updateSubscriptionInfo(null);
        updateNextPlan(null);
      } else if (!isSubscriptionUnchanged(data, subscription)) {
        updateSubscriptionInfo({
          ...data.subscription,
          is_unsubscribe: !!(
            (
              data.subscription.id &&
              data.subscription.status !== 'canceled' &&
              data.subscription.product_type === 'subscription' &&
              !['apple', 'google'].includes(data.subscription.pay_channel ?? '')
            ) // 苹果和谷歌的订阅不能在web取消
          ),
          plan_type: data.subscription.plan_type as SubsPlanType,
        });
        if (data.next_plan) {
          const { next_plan_type = undefined } = data.next_plan;
          updateNextPlan({
            ...data.next_plan,
            next_plan_type: ['standard_v2', 'premium_v2', 'advanced_v2'].includes(next_plan_type || '')
              ? (next_plan_type as SubsPlanType)
              : undefined,
          });
        } else {
          updateNextPlan(null);
        }
      }
      updateActiveTeamBundleProduct(data?.active_team_bundle_product ?? null);
      updateIsSubscriptionInitialized(true);
      // 已获取用户信息，则更新是否在赠送套餐中
      if (isUserInfoInitialized) {
        updateIsGiftPlan();
      }
    },
  });
  return {
    updateSubscription,
    updateSubscriptionAsync,
  };
};
